import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import EditIcon from '@mui/icons-material/Edit'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { EstadoDeLista, EstadoDeListaTipo } from '@/components/common/EstadoDeLista'
import { ChipNivel } from '@/components/nivel/ChipNivel'
import { DialogoNivel } from '@/components/nivel/DialogoNivel'
import { DialogoAtribuirNivel } from '@/components/nivel/DialogoAtribuirNivel'
import { HistoricoNivelDrawer } from '@/components/nivel/HistoricoNivelDrawer'
import { formatDataHoraNivel } from '@/components/nivel/formatacao'
import { authStore } from '@/store/authStore'
import { nivelConsultorService } from '@/services/nivelConsultorService'
import { permissoesNivelConsultor } from '@/utils/nivelConsultor'
import { AUTOR_PAPEL_ROTULO, AlteracaoGestao } from '@/types/campanha'
import { CANAL_LABEL, CanalComercial } from '@/types/statusReserva'
import {
  AVISO_NIVEL_INFORMATIVO,
  AtribuicaoNivelLinha,
  FiltroAtribuicoes,
  NivelConsultor,
  PaginaAtribuicoes,
  mensagemErroNivel,
  rotuloAlteracaoNivel,
} from '@/types/nivelConsultor'

type Aba = 'niveis' | 'atribuicoes' | 'historico'

const TITULO = 'Níveis'
const SUBTITULO = 'Nível do consultor: cadastro, atribuição e histórico'
const TAMANHO_PAGINA = 20

const estadoDoErro = (e: unknown): EstadoDeListaTipo =>
  (e as { status?: number })?.status === 403 ? 'semPermissao' : 'erro'

const rotuloCanal = (canal?: string | null) =>
  canal ? (CANAL_LABEL[canal as CanalComercial] ?? canal) : 'Sem canal'

const abreviar = (id: string) => (id.length > 8 ? `${id.slice(0, 8)}…` : id)

/**
 * Página "Níveis" (AN-10 §10; menu logo abaixo de "Equipes"). O nível é SÓ INFORMATIVO (Q26):
 * nada aqui mexe em comissão, metas, ranking ou acesso.
 *
 * - ADMIN: abas Níveis (catálogo), Atribuições (alterar nível com justificativa, histórico) e
 *   Histórico do cadastro (trilha `NIVEL_*`).
 * - GERENTE + GESTOR_EQUIPE: só a aba Atribuições, recortada pelo backend aos membros ativos da
 *   própria equipe, em leitura (sem justificativa, sem histórico — Q-AN10-3).
 * - Demais (GERENTE sem gestor, OPERADOR): sem permissão — a tela nem chama o backend.
 */
const NiveisConsultorPage: FC = () => {
  const usuario = authStore((s) => s.usuario)
  const perm = useMemo(() => permissoesNivelConsultor(usuario?.permissoes), [usuario])
  const ehAdmin = perm.administra

  const [aba, setAba] = useState<Aba>(ehAdmin ? 'niveis' : 'atribuicoes')

  // Catálogo (só ADMIN): alimenta a aba Níveis, o filtro e o seletor de atribuição.
  const [niveis, setNiveis] = useState<NivelConsultor[]>([])
  const [estadoNiveis, setEstadoNiveis] = useState<EstadoDeListaTipo | null>(ehAdmin ? 'carregando' : null)
  const [criteriosHabilitado, setCriteriosHabilitado] = useState(false)

  const carregarNiveis = useCallback(async () => {
    if (!ehAdmin) return
    setEstadoNiveis('carregando')
    try {
      const lista = await nivelConsultorService.listar(true)
      setNiveis(lista)
      setEstadoNiveis(lista.length === 0 ? 'vazio' : null)
    } catch (e) {
      setNiveis([])
      setEstadoNiveis(estadoDoErro(e))
    }
  }, [ehAdmin])

  useEffect(() => {
    if (!ehAdmin) return
    void carregarNiveis()
    // Flag de critérios: na dúvida (falha), a seção fica escondida.
    nivelConsultorService
      .configuracao()
      .then((c) => setCriteriosHabilitado(!!c?.criteriosAutomacaoHabilitado))
      .catch(() => setCriteriosHabilitado(false))
  }, [ehAdmin, carregarNiveis])

  if (!perm.vePagina) {
    return (
      <Box>
        <PageHeader title={TITULO} subtitle={SUBTITULO} />
        <Paper>
          <EstadoDeLista estado="semPermissao" minHeight={240} />
        </Paper>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader title={TITULO} subtitle={SUBTITULO} />

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        {AVISO_NIVEL_INFORMATIVO}
      </Alert>

      {perm.ehGestorEquipe && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          Você vê o nível atual dos consultores da sua equipe, só para consulta. A atribuição é feita pelo
          administrador.
        </Alert>
      )}

      {ehAdmin && (
        <Tabs value={aba} onChange={(_, v: Aba) => setAba(v)} sx={{ mb: 2 }} variant="scrollable">
          <Tab value="niveis" label="Níveis" />
          <Tab value="atribuicoes" label="Atribuições" />
          <Tab value="historico" label="Histórico do cadastro" />
        </Tabs>
      )}

      {ehAdmin && aba === 'niveis' && (
        <AbaNiveis
          niveis={niveis}
          estado={estadoNiveis}
          criteriosHabilitado={criteriosHabilitado}
          recarregar={carregarNiveis}
        />
      )}
      {aba === 'atribuicoes' && (
        <AbaAtribuicoes ehAdmin={ehAdmin} niveis={niveis} onAlterou={() => void carregarNiveis()} />
      )}
      {ehAdmin && aba === 'historico' && <AbaHistoricoCadastro niveis={niveis} />}
    </Box>
  )
}

// ───────────────────────── Aba Níveis (catálogo, ADMIN) ─────────────────────────

const AbaNiveis: FC<{
  niveis: NivelConsultor[]
  estado: EstadoDeListaTipo | null
  criteriosHabilitado: boolean
  recarregar: () => Promise<void>
}> = ({ niveis, estado, criteriosHabilitado, recarregar }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [dialogo, setDialogo] = useState<{ aberto: boolean; nivel: NivelConsultor | null }>({
    aberto: false,
    nivel: null,
  })
  const [ocupado, setOcupado] = useState(false)
  const ordenados = useMemo(() => [...niveis].sort((a, b) => a.ordem - b.ordem), [niveis])

  const mover = async (indice: number, delta: -1 | 1) => {
    const alvo = indice + delta
    if (alvo < 0 || alvo >= ordenados.length) return
    const ids = ordenados.map((n) => n.id)
    ;[ids[indice], ids[alvo]] = [ids[alvo], ids[indice]]
    setOcupado(true)
    try {
      await nivelConsultorService.reordenar(ids)
      enqueueSnackbar('Ordem atualizada', { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(mensagemErroNivel(e, 'Não foi possível reordenar'), { variant: 'error' })
    } finally {
      setOcupado(false)
      await recarregar()
    }
  }

  const alternarStatus = async (n: NivelConsultor) => {
    if (n.ativo) {
      const aviso =
        n.consultores > 0
          ? ` Os ${n.consultores} consultor(es) que já o têm mantêm o nível; ele só deixa de ser atribuível.`
          : ''
      if (!window.confirm(`Inativar o nível "${n.nome}"?${aviso}`)) return
    }
    setOcupado(true)
    try {
      await nivelConsultorService.alterarStatus(n.id, !n.ativo)
      enqueueSnackbar(n.ativo ? 'Nível inativado' : 'Nível reativado', { variant: 'success' })
      await recarregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroNivel(e, 'Não foi possível alterar o status'), { variant: 'error' })
    } finally {
      setOcupado(false)
    }
  }

  return (
    <>
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void recarregar()}>
          Atualizar
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogo({ aberto: true, nivel: null })}>
          Novo nível
        </Button>
      </Stack>
      <Paper>
        {estado ? (
          <EstadoDeLista
            estado={estado}
            minHeight={200}
            mensagem={
              estado === 'vazio' ? 'Nenhum nível cadastrado. Os níveis são definidos pelo Comercial.' : undefined
            }
            {...(estado === 'erro' ? { acao: { label: 'Tentar novamente', onClick: () => void recarregar() } } : {})}
          />
        ) : (
          <TableContainer>
            <Table size="small" aria-label="Níveis cadastrados">
              <TableHead>
                <TableRow>
                  <TableCell><strong>ORDEM</strong></TableCell>
                  <TableCell><strong>NÍVEL</strong></TableCell>
                  <TableCell><strong>DESCRIÇÃO</strong></TableCell>
                  <TableCell><strong>STATUS</strong></TableCell>
                  <TableCell><strong>CONSULTORES</strong></TableCell>
                  <TableCell align="right"><strong>AÇÕES</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ordenados.map((n, i) => (
                  <TableRow key={n.id} hover>
                    <TableCell>{n.ordem}</TableCell>
                    <TableCell>
                      <ChipNivel nome={n.nome} cor={n.cor} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {n.descricao}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={n.ativo ? 'Ativo' : 'Inativo'} color={n.ativo ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>{n.consultores}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Subir">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Subir ${n.nome}`}
                            disabled={ocupado || i === 0}
                            onClick={() => void mover(i, -1)}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Descer">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Descer ${n.nome}`}
                            disabled={ocupado || i === ordenados.length - 1}
                            onClick={() => void mover(i, 1)}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          aria-label={`Editar ${n.nome}`}
                          onClick={() => setDialogo({ aberto: true, nivel: n })}
                        >
                          <EditIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                      <Button size="small" disabled={ocupado} onClick={() => void alternarStatus(n)}>
                        {n.ativo ? 'Inativar' : 'Reativar'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <DialogoNivel
        aberto={dialogo.aberto}
        nivel={dialogo.nivel}
        criteriosHabilitado={criteriosHabilitado}
        onFechar={() => setDialogo({ aberto: false, nivel: null })}
        onSalvo={() => {
          enqueueSnackbar(dialogo.nivel ? 'Nível atualizado' : 'Nível criado', { variant: 'success' })
          setDialogo({ aberto: false, nivel: null })
          void recarregar()
        }}
      />
    </>
  )
}

// ───────────────────────── Aba Atribuições (ADMIN e gerente-gestor) ─────────────────────────

/** `todos` · `sem` (sem nível) · `inativo` (em nível inativo) · `nivel:<id>`. */
type FiltroNivel = string

const filtroParaParams = (f: FiltroNivel): FiltroAtribuicoes => {
  if (f === 'sem') return { semNivel: true }
  if (f === 'inativo') return { emNivelInativo: true }
  if (f.startsWith('nivel:')) return { nivelId: f.slice('nivel:'.length) }
  return {}
}

const AbaAtribuicoes: FC<{ ehAdmin: boolean; niveis: NivelConsultor[]; onAlterou: () => void }> = ({
  ehAdmin,
  niveis,
  onAlterou,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [busca, setBusca] = useState('')
  const [buscaAplicada, setBuscaAplicada] = useState('')
  const [filtro, setFiltro] = useState<FiltroNivel>('todos')
  const [pagina, setPagina] = useState(0)
  const [dados, setDados] = useState<PaginaAtribuicoes | null>(null)
  const [estado, setEstado] = useState<EstadoDeListaTipo | null>('carregando')
  const [alterando, setAlterando] = useState<AtribuicaoNivelLinha | null>(null)
  const [historico, setHistorico] = useState<AtribuicaoNivelLinha | null>(null)

  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaAplicada(busca.trim())
      setPagina(0)
    }, 300)
    return () => clearTimeout(t)
  }, [busca])

  const carregar = useCallback(async () => {
    setEstado('carregando')
    try {
      const r = await nivelConsultorService.atribuicoes({
        busca: buscaAplicada || undefined,
        ...filtroParaParams(filtro),
        page: pagina,
        size: TAMANHO_PAGINA,
      })
      setDados(r)
      setEstado(r.content.length === 0 ? 'vazio' : null)
    } catch (e) {
      setDados(null)
      setEstado(estadoDoErro(e))
    }
  }, [buscaAplicada, filtro, pagina])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const filtrando = !!buscaAplicada || filtro !== 'todos'
  const mensagemVazio = filtrando
    ? 'Nenhum consultor encontrado com esses filtros'
    : ehAdmin
      ? 'Nenhum consultor ativo'
      : 'Nenhum consultor na sua equipe'
  const ordenados = useMemo(() => [...niveis].sort((a, b) => a.ordem - b.ordem), [niveis])

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Buscar consultor"
            size="small"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            sx={{ minWidth: 240 }}
          />
          <TextField
            label="Nível"
            select
            size="small"
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value)
              setPagina(0)
            }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="sem">Sem nível</MenuItem>
            {ehAdmin && <MenuItem value="inativo">Em nível inativo</MenuItem>}
            {ehAdmin &&
              ordenados.map((n) => (
                <MenuItem key={n.id} value={`nivel:${n.id}`}>
                  {n.nome}
                  {n.ativo ? '' : ' (inativo)'}
                </MenuItem>
              ))}
          </TextField>
        </Stack>
      </Paper>

      <Paper>
        {estado ? (
          <EstadoDeLista
            estado={estado}
            minHeight={200}
            mensagem={estado === 'vazio' ? mensagemVazio : undefined}
            {...(estado === 'erro' ? { acao: { label: 'Tentar novamente', onClick: () => void carregar() } } : {})}
          />
        ) : (
          <>
            <TableContainer>
              <Table size="small" aria-label="Nível dos consultores">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>CONSULTOR</strong></TableCell>
                    <TableCell><strong>CANAL</strong></TableCell>
                    <TableCell><strong>NÍVEL</strong></TableCell>
                    {ehAdmin && <TableCell><strong>ATRIBUÍDO EM</strong></TableCell>}
                    {ehAdmin && <TableCell align="right"><strong>AÇÕES</strong></TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dados?.content.map((l) => {
                    const nome = l.nome || abreviar(l.consultorId)
                    return (
                      <TableRow key={l.consultorId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {nome}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={rotuloCanal(l.canal)} />
                        </TableCell>
                        <TableCell>
                          {l.nivel ? (
                            <ChipNivel
                              nome={l.nivel.nome}
                              cor={l.nivel.cor}
                              descricao={l.nivel.descricao}
                              inativo={ehAdmin && !l.nivel.ativo}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Sem nível
                            </Typography>
                          )}
                        </TableCell>
                        {ehAdmin && <TableCell>{l.nivel ? formatDataHoraNivel(l.atribuidoEm) : '—'}</TableCell>}
                        {ehAdmin && (
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Button size="small" aria-label={`Alterar nível de ${nome}`} onClick={() => setAlterando(l)}>
                              Alterar nível
                            </Button>
                            <Button size="small" aria-label={`Histórico de ${nome}`} onClick={() => setHistorico(l)}>
                              Histórico
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {dados && dados.totalElements > TAMANHO_PAGINA && (
              <TablePagination
                component="div"
                count={dados.totalElements}
                page={pagina}
                onPageChange={(_, p) => setPagina(p)}
                rowsPerPage={TAMANHO_PAGINA}
                rowsPerPageOptions={[TAMANHO_PAGINA]}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              />
            )}
          </>
        )}
      </Paper>

      {ehAdmin && (
        <>
          <DialogoAtribuirNivel
            linha={alterando}
            niveis={niveis}
            onFechar={() => setAlterando(null)}
            onSalvo={() => {
              enqueueSnackbar('Nível do consultor atualizado', { variant: 'success' })
              setAlterando(null)
              void carregar()
              onAlterou()
            }}
          />
          <HistoricoNivelDrawer linha={historico} onFechar={() => setHistorico(null)} />
        </>
      )}
    </>
  )
}

// ───────────────────────── Aba Histórico do cadastro (ADMIN) ─────────────────────────

const AbaHistoricoCadastro: FC<{ niveis: NivelConsultor[] }> = ({ niveis }) => {
  const [itens, setItens] = useState<AlteracaoGestao[]>([])
  const [estado, setEstado] = useState<EstadoDeListaTipo | null>('carregando')

  const carregar = useCallback(async () => {
    setEstado('carregando')
    try {
      const lista = await nivelConsultorService.alteracoes()
      setItens(lista)
      setEstado(lista.length === 0 ? 'vazio' : null)
    } catch (e) {
      setItens([])
      setEstado(estadoDoErro(e))
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const nomeNivel = (id: string) => niveis.find((n) => n.id === id)?.nome ?? abreviar(id)

  return (
    <Paper>
      {estado ? (
        <EstadoDeLista
          estado={estado}
          minHeight={200}
          mensagem={estado === 'vazio' ? 'Nenhuma alteração no cadastro de níveis' : undefined}
          {...(estado === 'erro' ? { acao: { label: 'Tentar novamente', onClick: () => void carregar() } } : {})}
        />
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Histórico do cadastro de níveis">
            <TableHead>
              <TableRow>
                <TableCell><strong>QUANDO</strong></TableCell>
                <TableCell><strong>ALTERAÇÃO</strong></TableCell>
                <TableCell><strong>NÍVEL</strong></TableCell>
                <TableCell><strong>DE → PARA</strong></TableCell>
                <TableCell><strong>AUTOR</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itens.map((h) => (
                <TableRow key={h.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDataHoraNivel(h.criadoEm)}</TableCell>
                  <TableCell>{rotuloAlteracaoNivel(h.tipo)}</TableCell>
                  <TableCell>{nomeNivel(h.entidadeId)}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word', maxWidth: 420 }}>
                    <Typography variant="caption">
                      {h.valorAnterior ?? '—'} → {h.valorNovo ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{AUTOR_PAPEL_ROTULO[h.autorPapel] ?? h.autorPapel}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}

export default NiveisConsultorPage
